import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import { type ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { config } from "@/config";
import { flightRecorder, type Summary } from "@/services/debug/FlightRecorder";
import { formatReport, formatTick, isTruncated } from "@/services/debug/formatReport";
import CornerStack from "@/ui/CornerStack";
import { zinc } from "@/ui/tokens";

const WINDOWS = [
  { label: "10s", ms: 10_000 },
  { label: "60s", ms: 60_000 },
  { label: "5m", ms: 300_000 },
] as const;

/** The HUD re-reads the recorder at most this often, so watching the page does not load it. */
const REFRESH_MS = 1000;
/** Busy time per second of window past which the pill turns amber, then red. */
const WARN_BUSY_PER_SECOND_MS = 50;
const ALERT_BUSY_PER_SECOND_MS = 200;
/** Sits above the update prompt in the bottom-right stack. */
const STACK_ORDER = 1;
/** MUI's tooltip layer sits below the corner stack's; lift it over the HUD panel. */
const TOOLTIP_SLOTS = { popper: { sx: { zIndex: 10_000 } } };

/** What each number means, shown on hover. */
const HELP = {
  pill:
    "Busy: how long the page spent reacting to work in the window: each burst runs from a store write or compute " +
    "until the next painted frame. Near the window length means the page never goes idle. Long: frames over 50ms. " +
    "Click to open the breakdown.",
  ticks: "Bursts of work: everything from one store write or compute until the browser next painted.",
  truncated: "The recorder keeps the last 5,000 bursts; older ones in this window were dropped, so totals cover less.",
  busy: "Total wall time of those bursts. The closer to the window length, the less the page is ever idle.",
  longFrames:
    "Frames the browser flagged as taking over 50ms. Blocking is the part past 50ms, when clicks and keys have to wait.",
  renders:
    "React commits under a <Profiler> (Dispatch, DriverView). Their time includes the computes that run during render.",
  rows: "Table rows that re-rendered, and rows their memo skipped because nothing they show changed.",
  windows: "Aggregate over the last 10 seconds, 60 seconds, or 5 minutes.",
  copy: "Copies this summary as Markdown, ready to paste into a chat with Claude.",
  reset: "Forgets everything recorded so far, to measure from now.",
  computes: "Store computes (useCollectionsCompute, useKeyedCompute) that re-ran, costliest first.",
  writes: "Rows written to the store, per collection: what set the computes off.",
  scripts: "Chrome's Long Animation Frames attribution: the callbacks the long frames spent their time in, summed.",
  worst:
    "The costliest bursts, whole: #id, wall time, [what caused it], table rows rendered/skipped. Writes read " +
    "written w / unchanged u / deleted d. A compute marked (same) returned the result it had before.",
} as const;

const COMPUTE_COLUMNS = [
  { label: "compute", help: "The compute's name: the hook or view that runs it, then what it builds." },
  { label: "runs", help: "How many times it ran." },
  { label: "changed", help: "Runs that returned a new result, so everything reading it re-rendered." },
  { label: "total", help: "Time spent in it across every run." },
  { label: "max", help: "Its slowest single run." },
];

const WRITE_COLUMNS = [
  { label: "collection", help: "The store collection written to." },
  { label: "flushes", help: "Bursts that wrote to it." },
  { label: "written", help: "Rows inserted or replaced. Each write re-runs every compute watching the collection." },
  {
    label: "unchanged",
    help: "Rows re-written with the values they already held, which the store left in place: no compute re-ran.",
  },
  { label: "deleted", help: "Rows removed." },
];

const SCRIPT_COLUMNS = [
  { label: "source", help: "The function, or the event that invoked it, and its file." },
  { label: "total", help: "Time the long frames spent in it." },
];

const cell = { px: 0.75, py: 0.25, textAlign: "right" as const, whiteSpace: "nowrap" as const };
const nameCell = { ...cell, textAlign: "left" as const, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" };

/**
 * Debug mode's on-page view of the flight recorder: a pill giving how busy the page has been, which opens onto where
 * the time went (computes, renders, long frames), what caused it (store writes), and the worst bursts whole. Every
 * number explains itself on hover. Copy report puts the same summary on the clipboard as Markdown, ready to paste into
 * a chat with an agent.
 */
export default function DebugHud() {
  const { workspaceId } = useParams();
  const [open, setOpen] = useState(false);
  const [windowMs, setWindowMs] = useState<number>(WINDOWS[0].ms);
  const [summary, setSummary] = useState<Summary>(() => flightRecorder.summary(windowMs));
  const [copied, setCopied] = useState(false);

  // Poll rather than subscribe: a subscription would re-render the HUD on every tick it is meant to observe.
  useEffect(() => {
    const refresh = () => setSummary(flightRecorder.summary(windowMs));
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [windowMs]);

  const busyPerSecond = summary.busyMs / (windowMs / 1000);
  const copyReport = () => {
    void navigator.clipboard.writeText(formatReport(summary, { workspaceId, stage: config.stage, url: location.href }));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <CornerStack order={STACK_ORDER}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
          fontFamily: "ui-monospace, monospace",
          fontSize: 11,
          color: zinc[200],
        }}
      >
        {open && (
          <Box
            sx={{
              width: 520,
              maxHeight: "70vh",
              overflow: "auto",
              bgcolor: "rgba(9, 9, 11, 0.94)",
              border: `1px solid ${zinc[700]}`,
              borderRadius: 1,
              p: 1.25,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
              {WINDOWS.map((option) => (
                <Help key={option.label} text={HELP.windows}>
                  <HudButton active={option.ms === windowMs} onClick={() => setWindowMs(option.ms)}>
                    {option.label}
                  </HudButton>
                </Help>
              ))}
              <Box sx={{ flex: 1 }} />
              <Help text={HELP.copy}>
                <HudButton onClick={copyReport}>{copied ? "Copied" : "Copy report"}</HudButton>
              </Help>
              <Help text={HELP.reset}>
                <HudButton onClick={() => flightRecorder.reset()}>Reset</HudButton>
              </Help>
            </Box>
            <Headline summary={summary} />
            <Section title="Computes" help={HELP.computes}>
              <Table
                columns={COMPUTE_COLUMNS}
                rows={summary.computes.map((compute) => [
                  compute.name,
                  compute.runs,
                  compute.changedRuns,
                  ms(compute.totalMs),
                  ms(compute.maxMs),
                ])}
              />
            </Section>
            <Section title="Store writes" help={HELP.writes}>
              <Table
                columns={WRITE_COLUMNS}
                rows={summary.writes.map((write) => [
                  write.collection,
                  write.flushes,
                  write.written,
                  write.unchanged,
                  write.deleted,
                ])}
              />
            </Section>
            {summary.frames.topScripts.length > 0 && (
              <Section title="Long-frame scripts" help={HELP.scripts}>
                <Table
                  columns={SCRIPT_COLUMNS}
                  rows={summary.frames.topScripts.map((script) => [script.source, ms(script.durationMs)])}
                />
              </Section>
            )}
            <Section title="Worst ticks" help={HELP.worst}>
              <Box component="pre" sx={{ m: 0, whiteSpace: "pre-wrap", color: zinc[300] }}>
                {summary.worst.flatMap(formatTick).join("\n") || "none"}
              </Box>
            </Section>
          </Box>
        )}
        <Help text={HELP.pill}>
          <ButtonBase
            onClick={() => setOpen((value) => !value)}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              fontFamily: "inherit",
              fontSize: "inherit",
              color: zinc[50],
              bgcolor: pillColor(busyPerSecond),
            }}
          >
            {`busy ${ms(summary.busyMs)} / ${windowMs / 1000}s · ${summary.frames.count} long`}
          </ButtonBase>
        </Help>
      </Box>
    </CornerStack>
  );
}

/** A hover explanation, on a span so any child can carry one. */
function Help({ text, children }: { text: string; children: ReactNode }) {
  return (
    <Tooltip title={text} placement="top" arrow disableInteractive slotProps={TOOLTIP_SLOTS}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

/** Text with a hover explanation, marked by a dotted underline. */
function Explained({ text, children }: { text: string; children: ReactNode }) {
  return (
    <Tooltip title={text} placement="top" arrow disableInteractive slotProps={TOOLTIP_SLOTS}>
      <Box component="span" sx={{ cursor: "help", borderBottom: `1px dotted ${zinc[500]}` }}>
        {children}
      </Box>
    </Tooltip>
  );
}

function Headline({ summary }: { summary: Summary }) {
  return (
    <Box sx={{ color: zinc[300], lineHeight: 1.8, display: "flex", flexWrap: "wrap", columnGap: 1.5 }}>
      {isTruncated(summary) && (
        <Explained text={HELP.truncated}>{`covers last ${(summary.coveredMs / 1000).toFixed(0)}s`}</Explained>
      )}
      <Explained text={HELP.ticks}>{`${summary.ticks} ticks`}</Explained>
      <Explained text={HELP.busy}>{`busy ${ms(summary.busyMs)}`}</Explained>
      <Explained text={HELP.longFrames}>
        {`long frames ${summary.frames.count} (${ms(summary.frames.totalMs)}, blocking ${ms(summary.frames.blockingMs)})`}
      </Explained>
      <Explained text={HELP.renders}>
        {`renders ${summary.renders.commits} (${ms(summary.renders.totalMs)}, max ${ms(summary.renders.maxMs)})`}
      </Explained>
      <Explained
        text={HELP.rows}
      >{`rows rendered ${summary.rows.rendered}, skipped ${summary.rows.skipped}`}</Explained>
    </Box>
  );
}

function Section({ title, help, children }: { title: string; help: string; children: ReactNode }) {
  return (
    <Box sx={{ mt: 1.25 }}>
      <Box sx={{ color: zinc[400], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
        <Explained text={help}>{title}</Explained>
      </Box>
      {children}
    </Box>
  );
}

function Table({ columns, rows }: { columns: { label: string; help: string }[]; rows: (string | number)[][] }) {
  if (rows.length === 0) return <Box sx={{ color: zinc[500] }}>none</Box>;
  return (
    <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <Box component="th" key={column.label} sx={{ ...(index === 0 ? nameCell : cell), color: zinc[500] }}>
              <Explained text={column.help}>{column.label}</Explained>
            </Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={String(row[0])}>
            {row.map((value, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional and fixed per table
              <Box component="td" key={index} sx={index === 0 ? nameCell : cell} title={String(value)}>
                {value}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function HudButton({ active, onClick, children }: { active?: boolean; onClick: () => void; children: string }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        px: 0.75,
        py: 0.25,
        borderRadius: 0.5,
        fontFamily: "inherit",
        fontSize: "inherit",
        color: active ? zinc[50] : zinc[300],
        bgcolor: active ? zinc[600] : zinc[800],
      }}
    >
      {children}
    </ButtonBase>
  );
}

function pillColor(busyPerSecond: number): string {
  if (busyPerSecond >= ALERT_BUSY_PER_SECOND_MS) return "#b91c1c";
  if (busyPerSecond >= WARN_BUSY_PER_SECOND_MS) return "#b45309";
  return zinc[800];
}

function ms(value: number): string {
  return `${value.toFixed(1)}ms`;
}
